import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../../components/Navbar';
import { localFamousKartsAPI, adminAPI } from '../../services/api';

/**
 * Admin: curate the "Local City Famous Karts" street-food collection shown as a round
 * button on the user-app home. Pick restaurants and, per restaurant, the menu CATEGORY
 * to auto-open when a customer taps it from the Karts screen (blank = show all).
 */
function LocalFamousKarts() {
  const [cfg, setCfg] = useState(null);                 // { enabled, title, subtitle }
  const [entries, setEntries] = useState([]);           // [{ restaurantId, category }]
  const [allRestaurants, setAllRestaurants] = useState([]);
  const [catsByRest, setCatsByRest] = useState({});     // { rid: [category,...] }
  const [query, setQuery] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);

  const nameOf = useCallback((rid) => {
    const r = allRestaurants.find((x) => String(x._id) === String(rid));
    return r ? r.name : rid;
  }, [allRestaurants]);

  // Lazy-load a restaurant's menu categories for the per-restaurant category picker.
  const loadCats = useCallback(async (rid) => {
    if (!rid) return;
    setCatsByRest((prev) => (prev[rid] ? prev : { ...prev, [rid]: null })); // null = loading
    try {
      const { data } = await adminAPI.getRestaurantMenu(rid);
      const cats = (data?.data?.allCategories) || [];
      setCatsByRest((prev) => ({ ...prev, [rid]: cats }));
    } catch (_) {
      setCatsByRest((prev) => ({ ...prev, [rid]: [] }));
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const [sRes, rRes] = await Promise.all([
        localFamousKartsAPI.getSettings(),
        adminAPI.getAllRestaurants({ limit: 300 }),
      ]);
      const d = sRes.data?.data || {};
      setCfg({ enabled: !!d.enabled, title: d.title || 'Local City Famous Karts', subtitle: d.subtitle || 'Street-food carts your city loves 🛒' });
      const ents = (d.restaurants || []).map((r) => ({ restaurantId: String(r.restaurantId), category: r.category || '' }));
      setEntries(ents);
      const rlist = rRes.data?.data?.restaurants || rRes.data?.data || [];
      setAllRestaurants(rlist);
      ents.forEach((e) => loadCats(e.restaurantId));
    } catch (e) { setMsg('Failed to load settings'); }
  }, [loadCats]);
  useEffect(() => { load(); }, [load]);

  const isSelected = (rid) => entries.some((e) => String(e.restaurantId) === String(rid));
  const toggleRestaurant = (rid) => {
    const key = String(rid);
    if (isSelected(key)) {
      setEntries((prev) => prev.filter((e) => String(e.restaurantId) !== key));
    } else {
      setEntries((prev) => [...prev, { restaurantId: key, category: '' }]);
      loadCats(key);
    }
  };
  const setEntryCategory = (rid, category) => {
    setEntries((prev) => prev.map((e) => (String(e.restaurantId) === String(rid) ? { ...e, category } : e)));
  };
  const move = (idx, dir) => {
    setEntries((prev) => {
      const a = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= a.length) return a;
      [a[idx], a[j]] = [a[j], a[idx]];
      return a;
    });
  };

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await localFamousKartsAPI.updateSettings({ ...cfg, restaurants: entries });
      setMsg('Saved — live in the app.');
      load();
    } catch (e) { setMsg(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setCfg((prev) => ({ ...prev, [k]: v }));

  if (!cfg) return (<div><Navbar /><div style={{ padding: 24 }}>Loading…</div></div>);

  const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: '0 4px 10px rgba(0,0,0,0.05)' };
  const input = { width: '100%', padding: '9px 11px', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
  const label = { display: 'block', fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 4 };

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6f8' }}>
      <Navbar />
      <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', margin: '0 0 4px' }}>🛒 Local City Famous Karts</h1>
        <p style={{ color: '#64748B', fontSize: 14, margin: '0 0 18px' }}>
          Curate a street-food carts/stalls collection shown as a round button on the app home. Pick restaurants and, for each, the menu <b>category</b> to auto-open when a customer taps it here (blank = show the whole menu).
        </p>

        <div style={card}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: '#334155' }}>
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            Enabled (show the Local City Famous Karts button on the home screen)
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
            <div><label style={label}>Title</label><input style={input} value={cfg.title} onChange={(e) => set('title', e.target.value)} maxLength={60} /></div>
            <div><label style={label}>Subtitle</label><input style={input} value={cfg.subtitle} onChange={(e) => set('subtitle', e.target.value)} maxLength={120} /></div>
          </div>
        </div>

        {/* Selected restaurants (ordered) with per-restaurant category */}
        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>In the collection ({entries.length})</div>
          {entries.length === 0 && <div style={{ color: '#94a3b8', fontSize: 14 }}>No restaurants yet — add some below.</div>}
          {entries.map((e, i) => {
            const cats = catsByRest[e.restaurantId];
            return (
              <div key={e.restaurantId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f2f2f2' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <button onClick={() => move(i, -1)} disabled={i === 0} style={{ border: 'none', background: 'none', cursor: 'pointer', color: i === 0 ? '#cbd5e1' : '#64748b', lineHeight: 1 }}>▲</button>
                  <button onClick={() => move(i, 1)} disabled={i === entries.length - 1} style={{ border: 'none', background: 'none', cursor: 'pointer', color: i === entries.length - 1 ? '#cbd5e1' : '#64748b', lineHeight: 1 }}>▼</button>
                </div>
                <div style={{ flex: 1, fontWeight: 600, fontSize: 14 }}>{i + 1}. {nameOf(e.restaurantId)}</div>
                <div style={{ minWidth: 200 }}>
                  <select style={{ ...input, padding: '6px 8px' }} value={e.category} onChange={(ev) => setEntryCategory(e.restaurantId, ev.target.value)}>
                    <option value="">— All categories —</option>
                    {cats === null && <option disabled>Loading…</option>}
                    {Array.isArray(cats) && cats.map((c) => <option key={c} value={c}>{c}</option>)}
                    {/* keep a saved category even if not in the loaded list */}
                    {e.category && Array.isArray(cats) && !cats.includes(e.category) && <option value={e.category}>{e.category}</option>}
                  </select>
                </div>
                <button onClick={() => toggleRestaurant(e.restaurantId)} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 8, padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Remove</button>
              </div>
            );
          })}
        </div>

        {/* Restaurant picker */}
        <div style={card}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Add restaurants</div>
          <input style={{ ...input, marginBottom: 8 }} placeholder="Filter restaurants…" value={query} onChange={(e) => setQuery(e.target.value)} />
          <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
            {allRestaurants
              .filter((r) => !query || (r.name || '').toLowerCase().includes(query.toLowerCase()))
              .map((r) => (
                <label key={r._id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={isSelected(r._id)} onChange={() => toggleRestaurant(r._id)} />
                  <span>{r.name}</span>
                </label>
              ))}
            {allRestaurants.length === 0 && <span style={{ color: '#94a3b8' }}>No restaurants loaded.</span>}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={save} disabled={saving} style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          {msg && <span style={{ fontSize: 14, color: msg.includes('Saved') ? '#16a34a' : '#dc2626' }}>{msg}</span>}
        </div>
      </div>
    </div>
  );
}

export default LocalFamousKarts;
